import { Injectable } from '@angular/core';
import { GenderTypes } from 'src/app/feed-advice/models/feed-advice.model';
import { Discount } from 'src/app/settings/models/settings.model';
import {
  FEED_PROFIT_ENERGY_VALUES,
  FEED_PROFIT_MANURE_VALUES,
} from 'src/assets/data/feedProfitConstants';

import { FeedProfitCalculations } from './feed-profit-calculations.service';
import {
  Calculation,
  CountryOptions,
  FeederType,
  GenderOptions,
  QuickSimulation,
} from '../models/feed-profit.model';

@Injectable({
  providedIn: 'root',
})
export class FeedProfitQuickSimulationsCalculationService {
  constructor(private readonly genericCalculationsService: FeedProfitCalculations) {}

  // Calculates J17
  getOccupancyRate(
    calculation: Calculation,
    simulation: QuickSimulation,
    livingWeight?: number,
    totalgrowth?: number,
  ) {
    livingWeight = livingWeight ?? this.getLivingWeight(calculation, simulation);
    totalgrowth =
      totalgrowth ||
      this.getTotalGrowth(
        calculation,
        simulation,
        livingWeight,
        this.getAmountOfDays(calculation, simulation),
      );

    const slaughteredWeight = livingWeight - simulation.startWeight;
    const growthPerSlaughteredKg = slaughteredWeight / (totalgrowth * 0.001);

    const day2GrowthPerSlaughteredKgCalculation =
      growthPerSlaughteredKg +
      this.genericCalculationsService.toExcelPercentage(
        calculation.extendedBaseLine.deliveries[0].percentage,
      ) *
        calculation.extendedBaseLine.deliveries[1].days;

    return calculation.extendedBaseLine.deliveries[2].percentage > 0
      ? this.genericCalculationsService.toVisualPercentage(
          growthPerSlaughteredKg /
            (day2GrowthPerSlaughteredKgCalculation +
              (this.genericCalculationsService.toExcelPercentage(
                calculation.extendedBaseLine.deliveries[0].percentage,
              ) +
                this.genericCalculationsService.toExcelPercentage(
                  calculation.extendedBaseLine.deliveries[1].percentage,
                )) *
                calculation.extendedBaseLine.deliveries[2].days +
              calculation.extendedBaseLine.cleaning),
        )
      : this.genericCalculationsService.toVisualPercentage(
          growthPerSlaughteredKg /
            (day2GrowthPerSlaughteredKgCalculation + calculation.extendedBaseLine.cleaning),
        );
  }

  // Calculates N51
  getPacketPrice(
    calculation: Calculation,
    simulation: QuickSimulation,
    livingWeight?: number,
    calculationLivingWeight?: number,
  ) {
    livingWeight = livingWeight ?? this.getLivingWeight(calculation, simulation);
    calculationLivingWeight =
      calculationLivingWeight ?? this.getCalculationLivingWeight(calculation);

    let totalKg = 0;
    let totalCosts = 0;

    const allFeeders = [
      ...calculation.feeders.map((f) => ({ ...f, isCalculated: false })),
      { ...calculation.calculatedFeeder, isCalculated: true },
    ];

    const divergingEnergyFromGrowthStartStartWeight =
      this.getDivergingEnergyFromGrowthStartStartWeight(calculation, simulation);

    const divergingEnergyFromGrowthEndDeliveredWeight =
      this.getDivergingEnergyFromGrowthEndDeliveredWeight(
        calculation,
        simulation,
        livingWeight,
        calculationLivingWeight,
      );

    const mappedFeeders = allFeeders.map((feeder) => {
      const feederFeed =
        feeder.genderFeedRatios
          .map((ratio) => ratio.feed)
          .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0) / feeder.genderFeedRatios.length;

      let weight = feederFeed;

      switch (feeder.type) {
        case FeederType.START_FEEDER:
          weight = feederFeed + divergingEnergyFromGrowthStartStartWeight;
          break;
        case FeederType.GROWTH_FEEDER:
          break;
        default:
          if (feeder.isCalculated) {
            weight = feederFeed + divergingEnergyFromGrowthEndDeliveredWeight;
          }
      }

      return {
        kg: weight,
        costs: feeder.product.grossPricePer100Kg,
      };
    });
    mappedFeeders.forEach((feeder) => {
      totalCosts += feeder.costs * feeder.kg * 0.01;
      totalKg += feeder.kg;
    });

    return (
      totalCosts / (totalKg * 0.01) +
      (simulation.feedPackagePrice - (calculation.comparisonSimulation?.feedPackagePrice ?? 0))
    );
  }

  // Calculates N51
  getPacketPriceForCurrentSimulation(
    calculation: Calculation,
    simulation: QuickSimulation,
    livingWeight?: number,
    calculationLivingWeight?: number,
  ) {
    livingWeight = livingWeight ?? this.getLivingWeight(calculation, simulation);
    calculationLivingWeight =
      calculationLivingWeight ?? this.getCalculationLivingWeight(calculation);

    let totalKg = 0;
    let totalCosts = 0;

    const allFeeders = [...calculation.feeders, calculation.calculatedFeeder];

    const divergingEnergyFromGrowthStartStartWeight =
      this.getDivergingEnergyFromGrowthStartStartWeight(calculation, simulation);

    const divergingEnergyFromGrowthEndDeliveredWeight =
      this.getDivergingEnergyFromGrowthEndDeliveredWeight(
        calculation,
        simulation,
        livingWeight,
        calculationLivingWeight,
      );

    const mappedFeeders = allFeeders.map((feeder) => {
      const feederFeed =
        feeder.genderFeedRatios
          .map((ratio) => ratio.feed)
          .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0) / feeder.genderFeedRatios.length;

      return {
        kg:
          feeder.type === FeederType.START_FEEDER
            ? feederFeed + divergingEnergyFromGrowthStartStartWeight
            : feeder.type === FeederType.GROWTH_FEEDER
              ? feederFeed
              : feederFeed + divergingEnergyFromGrowthEndDeliveredWeight,
        costs: feeder.product.grossPricePer100Kg,
      };
    });

    mappedFeeders.forEach((feeder) => {
      totalCosts += feeder.costs * feeder.kg * 0.01;
      totalKg += feeder.kg;
    });

    const feedPackagePrice = (totalCosts / totalKg) * 100;
    return feedPackagePrice;
  }

  // Calculates H51
  getSpecificFeederFeedPrice(calculation: Calculation, feedType: FeederType) {
    const allFeeders = [...calculation.feeders, calculation.calculatedFeeder];

    const feedersOfType = allFeeders.filter((feeder) => feeder.type === feedType);
    let feedExpenses = 0;
    let feedKg = 0;
    feedersOfType
      .map((feeder) => {
        const feederFeed =
          feeder.genderFeedRatios
            .map((ratio) => ratio.feed)
            .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0) / feeder.genderFeedRatios.length;

        return { price: feederFeed * feeder.product.grossPricePer100Kg * 0.01, kg: feederFeed };
      })
      .forEach((feeder) => {
        feedExpenses += feeder.price;
        feedKg += feeder.kg;
      });

    return (feedExpenses / feedKg) * 100;
  }

  // Calculates J31
  getEffectOnFeedCostsStartWeightHas(calculation: Calculation, simulation: QuickSimulation) {
    const startFeederPrice = this.getSpecificFeederFeedPrice(calculation, FeederType.START_FEEDER); //H51
    const allFeeders = [...calculation.feeders, calculation.calculatedFeeder];

    const startFeeders = allFeeders.filter((feeder) => feeder.type === FeederType.START_FEEDER);
    const avgEnergyStartFeeders =
      startFeeders
        .map((feeder) => feeder.product.ewKg)
        .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0) / startFeeders.length;
    const divergingEnergyStartWeight = this.getDivergingEnergyFromGrowthStartStartWeight(
      calculation,
      simulation,
    );

    return ((startFeederPrice / 100) * divergingEnergyStartWeight) / avgEnergyStartFeeders;
  }

  // Calculates J27
  getEffectOnFeedCostsSlaughteredWeightHas(calculation: Calculation, simulation: QuickSimulation) {
    const endFeederPrice = this.getSpecificFeederFeedPrice(calculation, FeederType.END_FEEDER); //H53
    const allFeeders = [...calculation.feeders, calculation.calculatedFeeder];

    const endFeeders = allFeeders.filter((feeder) => feeder.type === FeederType.END_FEEDER);
    const avgEnergyEndFeeders =
      endFeeders
        .map((feeder) => feeder.product.ewKg)
        .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0) / endFeeders.length;
    const divergingEnergyEndWeight = this.getDivergingEnergyFromGrowthEndDeliveredWeight(
      calculation,
      simulation,
    );

    return ((endFeederPrice / 100) * divergingEnergyEndWeight) / avgEnergyEndFeeders;
  }

  // Calculates J30
  getPacketPriceForFeedCosts(
    calculation: Calculation,
    simulation: QuickSimulation,
    livingWeight?: number,
    calculationLivingWeight?: number,
  ) {
    livingWeight = livingWeight ?? this.getLivingWeight(calculation, simulation);
    calculationLivingWeight =
      calculationLivingWeight ?? this.getCalculationLivingWeight(calculation);
    let totalKg = 0;
    let totalCosts = 0;
    let averageEnergyStartFeeders = 0;
    let averageEnergyEndFeeders = 0;

    const allFeeders = [
      ...calculation.feeders.map((f) => ({ ...f, isCalculated: false })),
      { ...calculation.calculatedFeeder, isCalculated: true },
    ];

    allFeeders
      .map((feeder) => {
        const feederFeed =
          feeder.genderFeedRatios
            .map((ratio) => ratio.feed)
            .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0) / feeder.genderFeedRatios.length;

        if (feeder.type === FeederType.START_FEEDER) {
          averageEnergyStartFeeders += feeder.product.ewKg;
        }
        if (feeder.type === FeederType.END_FEEDER) {
          averageEnergyEndFeeders += feeder.product.ewKg;
        }
        return {
          kg:
            feeder.type === FeederType.START_FEEDER
              ? feederFeed +
                this.getDivergingEnergyFromGrowthStartStartWeight(
                  calculation,
                  calculation.comparisonSimulation,
                )
              : feeder.type === FeederType.GROWTH_FEEDER
                ? feederFeed
                : feeder.isCalculated
                  ? feederFeed +
                    this.getDivergingEnergyFromGrowthEndDeliveredWeight(
                      calculation,
                      calculation.comparisonSimulation,
                      undefined,
                      calculationLivingWeight,
                    )
                  : feederFeed,
          costs: feeder.product.grossPricePer100Kg,
        };
      })
      .forEach((feeder) => {
        totalCosts += feeder.costs * feeder.kg * 0.01;
        totalKg += feeder.kg;
      }); //totalcosts = j50

    averageEnergyStartFeeders =
      averageEnergyStartFeeders /
      allFeeders.filter((f) => f.type === FeederType.START_FEEDER).length;
    averageEnergyEndFeeders =
      averageEnergyEndFeeders / allFeeders.filter((f) => f.type === FeederType.END_FEEDER).length;

    const diverGingAmountOfGrowthFromStartWeight =
      this.getDivergingEnergyFromGrowthStartStartWeight(calculation, simulation); //j22
    const divergingEnergyFromGrowthEndDeliveredWeight =
      this.getDivergingEnergyFromGrowthEndDeliveredWeight(
        calculation,
        simulation,
        livingWeight,
        this.getCalculationLivingWeight(calculation),
      ); //j23
    //C45
    const costEffectOfStartWeight = this.getEffectOnFeedCostsStartWeightHas(
      calculation,
      simulation,
    ); //j26
    const costEffectOfSlaughteredWeight = this.getEffectOnFeedCostsSlaughteredWeightHas(
      calculation,
      simulation,
    ); //j27

    const costEffects = totalCosts + costEffectOfStartWeight + costEffectOfSlaughteredWeight;
    const kgWithFeederEnergies =
      totalKg +
      diverGingAmountOfGrowthFromStartWeight / averageEnergyStartFeeders +
      divergingEnergyFromGrowthEndDeliveredWeight / averageEnergyEndFeeders;
    const feedPackagePriceDifference =
      simulation.calculatedFeedPackagePrice -
      calculation.comparisonSimulation.calculatedFeedPackagePrice;
    const finalPacketPrice =
      costEffects / (kgWithFeederEnergies / 100) + feedPackagePriceDifference;

    return finalPacketPrice;
  }

  getDivergingEnergyFromGrowthStartStartWeight(
    calculation: Calculation,
    simulation: QuickSimulation,
  ) {
    const energyConversion = this.getEnergyConvertedBeginProcedure(calculation, simulation);

    return simulation.startWeight < calculation.extendedBaseLine.startWeight
      ? (simulation.startWeight - calculation.extendedBaseLine.startWeight) * energyConversion * -1
      : (calculation.extendedBaseLine.startWeight - simulation.startWeight) * energyConversion;
  }

  getDivergingEnergyFromGrowthEndDeliveredWeight(
    calculation: Calculation,
    simulation: QuickSimulation,
    livingWeight?: number,
    calculationLivingWeight?: number,
  ) {
    livingWeight = livingWeight ?? this.getLivingWeight(calculation, simulation);
    calculationLivingWeight =
      calculationLivingWeight ?? this.getCalculationLivingWeight(calculation);
    const energyConversion = this.getEnergyConvertedEndProcedure(calculation);

    /*
    original calculation:
calculation.genders === GenderOptions.BOAR_GILT
      ? (livingWeight - livingWeight) * energyConversion
      : calculation.genders === GenderOptions.BARROW_GILT &&
        calculation.country === CountryOptions.NL
      ? (calculationLivingWeight - livingWeight) * energyConversion
      : (livingWeight - livingWeight) * energyConversion;
      changed to return zero since the sheet uses J11 and G11 for these different livingweights
      These fields respectivly both use invulformulier's Q62 and F62, these fields appear to be
      the same but probably differ in extended so this is why this weird construction is left in here
      for now.
    */

    return calculation.genders === GenderOptions.BOAR_GILT
      ? (livingWeight - livingWeight) * energyConversion
      : calculation.genders === GenderOptions.BARROW_GILT &&
          calculation.country === CountryOptions.NL
        ? (livingWeight - calculationLivingWeight) * energyConversion
        : (livingWeight - livingWeight) * energyConversion;
  }

  getEnergyValueFromLookup(value: number, returnValues: number[]) {
    for (let i = 0; i < FEED_PROFIT_ENERGY_VALUES.lowerbounds.length; i++) {
      if (
        value >= FEED_PROFIT_ENERGY_VALUES.lowerbounds[i] &&
        value <= FEED_PROFIT_ENERGY_VALUES.upperbounds[i]
      ) {
        return returnValues[i];
      }
    }
    return 0;
  }

  getEnergyConvertedBeginProcedure(calculation: Calculation, simulation: QuickSimulation) {
    const averageEnergyConversion = calculation.extendedBaseLine.averageEnergyConversion;

    return simulation.startWeight > 25
      ? this.getEnergyValueFromLookup(
          averageEnergyConversion,
          FEED_PROFIT_ENERGY_VALUES.values.startMoreThan25,
        )
      : this.getEnergyValueFromLookup(
          averageEnergyConversion,
          FEED_PROFIT_ENERGY_VALUES.values.startLessThan25,
        );
  }

  getEnergyConvertedEndProcedure(calculation: Calculation) {
    const averageEnergyConversion = calculation.extendedBaseLine.averageEnergyConversion;
    let energyConversionFromTable = this.getEnergyValueFromLookup(
      averageEnergyConversion,
      FEED_PROFIT_ENERGY_VALUES.values.endBoar,
    );

    if (calculation.genders === GenderOptions.BARROW_GILT) {
      energyConversionFromTable += 0.2;
    }
    return energyConversionFromTable;
  }
  getAverageDeliveredGrowFinisher(
    calculation: Calculation,
    simulation: QuickSimulation,
    occupancyRate?: number,
  ) {
    occupancyRate = occupancyRate ?? this.getOccupancyRate(calculation, simulation);

    return calculation.companyDetails.amountOfGrowFinisher * (occupancyRate * 0.01);
  }

  // Calculates J11
  getLivingWeight(calculation: Calculation, simulation: QuickSimulation) {
    return simulation.slaughteredWeightInKg * this.getGenderedBasedMultiplier(calculation) + 5;
  }

  // Calculates F11
  getCalculationLivingWeight(calculation: Calculation) {
    return (
      calculation?.extendedBaseLine?.slaughteredWeightInKg *
        this.getGenderedBasedMultiplier(calculation) +
      5
    );
  }

  // Calculates J12
  // More / less diverging days start weight.
  getDivergingDaysStartWeight(calculation: Calculation, simulation: QuickSimulation) {
    const divergingDaysStartWeight =
      simulation.startWeight < calculation.comparisonSimulation.startWeight
        ? ((simulation.startWeight - calculation.comparisonSimulation.startWeight) /
            (calculation.baseLine.growthStartProcedure * 0.001)) *
          -1
        : (calculation.comparisonSimulation.startWeight - simulation.startWeight) /
          (calculation.baseLine.growthStartProcedure * 0.001);
    return divergingDaysStartWeight;
  }

  // Calculates J13
  // More / less diverging days slaughtered weight.
  getDivergingDaysSlaughteredWeight(
    calculation: Calculation,
    livingWeight: number,
    calculationLivingWeight: number,
  ) {
    const divergingDaysSlaughteredWeight =
      Math.abs(livingWeight - calculationLivingWeight) /
      (calculation.baseLine.growthEndProcedure * 0.001);

    return divergingDaysSlaughteredWeight;
  }

  //J13
  // More / less diverging days growth.
  getDivergingDaysGrowth(calculation: Calculation, livingWeight: number) {
    const divergingDaysGrowth =
      (((livingWeight - calculation.extendedBaseLine.startWeight) /
        calculation.extendedBaseLine.growth) *
        0.001 -
        ((livingWeight - calculation.extendedBaseLine.startWeight) /
          calculation.extendedBaseLine.growth) *
          0.001) *
      0.001;
    return divergingDaysGrowth;
  }

  // Calculates J15
  getAmountOfDays(
    calculation: Calculation,
    simulation: QuickSimulation,
    livingWeight?: number,
    calculationLivingWeight?: number,
  ) {
    livingWeight = livingWeight ?? this.getLivingWeight(calculation, simulation);
    calculationLivingWeight =
      calculationLivingWeight ?? this.getCalculationLivingWeight(calculation);

    const divergingDaysStartWeight = this.getDivergingDaysStartWeight(calculation, simulation);
    const divergingDaysSlaughteredWeight = this.getDivergingDaysSlaughteredWeight(
      calculation,
      livingWeight,
      calculationLivingWeight,
    );
    const divergingDaysGrowth = this.getDivergingDaysGrowth(calculation, livingWeight);

    const result =
      (calculationLivingWeight - calculation.extendedBaseLine.startWeight) /
        (calculation.extendedBaseLine.growth * 0.001) +
      divergingDaysStartWeight +
      divergingDaysSlaughteredWeight +
      divergingDaysGrowth;

    return result;
  }

  // Calculates J16
  getTotalGrowth(
    calculation: Calculation,
    simulation: QuickSimulation,
    livingWeight: number,
    amountOfDays: number,
  ) {
    livingWeight = livingWeight ?? this.getLivingWeight(calculation, simulation);
    amountOfDays = amountOfDays ?? this.getAmountOfDays(calculation, simulation, livingWeight);

    return ((livingWeight - simulation.startWeight) / amountOfDays) * 1000;
  }

  getGenderedBasedMultiplier(calculation: Calculation) {
    let genderBasedMultiplier = 1;
    if (
      calculation.genders === GenderOptions.BOAR_GILT &&
      calculation.country === CountryOptions.NL
    ) {
      genderBasedMultiplier = 1.21;
    } else if (
      calculation.genders === GenderOptions.BARROW_GILT &&
      calculation.country === CountryOptions.NL
    ) {
      genderBasedMultiplier = 1.2;
    } else if (
      calculation.genders === GenderOptions.BOAR_GILT &&
      calculation.country === CountryOptions.DU
    ) {
      genderBasedMultiplier = 1.225;
    } else if (
      calculation.genders === GenderOptions.BARROW_GILT &&
      calculation.country === CountryOptions.DU
    ) {
      genderBasedMultiplier = 1.215;
    } else if (
      calculation.genders === GenderOptions.BARROW_GILT &&
      calculation.country === CountryOptions.BIO
    ) {
      genderBasedMultiplier = 1.235;
    }
    return genderBasedMultiplier;
  }

  // Calculates J21
  getRounds(
    calculation: Calculation,
    simulation: QuickSimulation,
    amountOfDays?: number,
    occupancyRate?: number,
  ) {
    amountOfDays = amountOfDays ?? this.getAmountOfDays(calculation, simulation);
    occupancyRate = occupancyRate ?? this.getOccupancyRate(calculation, simulation);

    return (365 / amountOfDays) * (occupancyRate * 0.01);
  }

  // Calculates S50
  getPigFeedSum(
    calculation: Calculation,
    simulation: QuickSimulation,
    isComparison = false,
    livingWeight?: number,
  ) {
    const divergingEnergyStart = this.getDivergingEnergyFromGrowthStartStartWeight(
      calculation,
      simulation,
    );
    const divergingEnergyEnd = this.getDivergingEnergyFromGrowthEndDeliveredWeight(
      calculation,
      simulation,
      livingWeight,
    );
    let PigFeedSum = 0;
    const allFeeders = [
      ...calculation.feeders.map((f) => ({ ...f, isCalculated: false })),
      { ...calculation.calculatedFeeder, isCalculated: true },
    ];

    if (!isComparison) {
      allFeeders
        .map((feeder) => {
          const feederFeed = feeder.genderFeedRatios
            .map((ratio) => ratio.feed)
            .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0);

          let returnFeed = 0;
          switch (feeder.type) {
            case FeederType.START_FEEDER:
              returnFeed = feederFeed / feeder.genderFeedRatios.length + divergingEnergyStart;
              break;
            case FeederType.GROWTH_FEEDER:
              returnFeed = feederFeed / feeder.genderFeedRatios.length;
              break;

            case FeederType.END_FEEDER:
              returnFeed = feederFeed / feeder.genderFeedRatios.length;
              if (feeder.isCalculated) {
                returnFeed += divergingEnergyEnd;
              }
              break;
            default:
              break;
          }
          return returnFeed;
        })
        .forEach((feederAvg) => (PigFeedSum += feederAvg ?? 0));
    } else {
      allFeeders
        .map((feeder) =>
          feeder.genderFeedRatios
            .map((ratio) => ratio.feed ?? 0)
            .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0),
        )
        .forEach((feed) => (PigFeedSum += feed ?? 0));
    }

    return PigFeedSum ?? 0;
  }

  // Calculates T53
  getFeedConversion(simulation: QuickSimulation, PigFeedSum: number, livingWeight: number) {
    return PigFeedSum / (livingWeight - simulation.startWeight);
  }

  getFeedPrice(
    calculation: Calculation,
    simulation: QuickSimulation,
    PigFeedSum: number,
    livingWeight: number,
    feedPacketPrice?: number,
  ) {
    feedPacketPrice =
      feedPacketPrice ||
      this.getPacketPrice(
        calculation,
        simulation,
        livingWeight,
        this.getCalculationLivingWeight(calculation),
      );

    return (feedPacketPrice * PigFeedSum * 0.01) / (livingWeight - simulation.startWeight);
  }

  // Calculates J29
  getMaxEnergy(calculation: Calculation) {
    let maxEnergy = 0;
    const allFeeders = [...calculation.feeders, calculation.calculatedFeeder];

    allFeeders
      .map((feeder) => {
        const feederFeed = feeder.genderFeedRatios
          .map((ratio) => ratio.feed)
          .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0);

        return {
          product: feeder.product,
          averageKg: feederFeed / feeder.genderFeedRatios.length,
        };
      })
      .forEach((feeder) => (maxEnergy += feeder.product.ewKg * feeder.averageKg));
    return maxEnergy;
  }

  getEnergyConversionGenderSpecific(calculation: Calculation, genderType: GenderTypes) {
    const averageEnergyConversion = calculation.extendedBaseLine.averageEnergyConversion;
    return calculation.genders === GenderOptions.BOAR_GILT &&
      calculation.country === CountryOptions.NL
      ? genderType === GenderTypes.BOAR
        ? averageEnergyConversion - 0.1
        : averageEnergyConversion + 0.1
      : genderType === GenderTypes.GILT
        ? averageEnergyConversion - 0.075
        : averageEnergyConversion + 0.075;
  }

  getFeedGrowFinisherNeeds(
    calculation: Calculation,
    genderType: GenderTypes,
    calculationLivingWeight?: number,
  ) {
    calculationLivingWeight =
      calculationLivingWeight ?? this.getCalculationLivingWeight(calculation);

    const energyConversion = this.getEnergyConversionGenderSpecific(calculation, genderType);
    return (calculationLivingWeight - calculation.extendedBaseLine.startWeight) * energyConversion;
  }

  getFeedOfGrowFinisherGender(calculation: Calculation, genderType: GenderTypes) {
    let feedOfGrowFinisherGender = 0;

    calculation.feeders
      .map((feeder) => {
        const ratio = feeder.genderFeedRatios.filter((ratio) => ratio.name === genderType)[0];

        return ratio?.feed * feeder.product.ewKg;
      })
      .forEach((feeder) => (feedOfGrowFinisherGender += feeder));
    if (!feedOfGrowFinisherGender) {
      feedOfGrowFinisherGender = 0;
    }
    return feedOfGrowFinisherGender;
  }
  calculateRemainingEndFeederRatios(calculation: Calculation, livingWeight?: number) {
    livingWeight = livingWeight ?? this.getCalculationLivingWeight(calculation);
    const calculatedFeederEnergy = calculation.calculatedFeeder?.product?.ewKg ?? 0;
    const amountOfNeededTotalFeedBoar =
      this.getFeedGrowFinisherNeeds(calculation, GenderTypes.BOAR, livingWeight) ?? 0;
    const amountOfNeededTotalFeedBarrow =
      this.getFeedGrowFinisherNeeds(calculation, GenderTypes.BARROW, livingWeight) ?? 0;
    const amountOfNeededTotalFeedGilt =
      this.getFeedGrowFinisherNeeds(calculation, GenderTypes.GILT, livingWeight) ?? 0;
    const gilttest = this.getFeedOfGrowFinisherGender(calculation, GenderTypes.GILT);
    const amountOfEndFeedBoar =
      (amountOfNeededTotalFeedBoar -
        this.getFeedOfGrowFinisherGender(calculation, GenderTypes.BOAR)) /
      calculatedFeederEnergy;
    const amountOfEndFeedGilt = (amountOfNeededTotalFeedGilt - gilttest) / calculatedFeederEnergy;
    const amountOfEndFeedBarrow =
      (amountOfNeededTotalFeedBarrow -
        this.getFeedOfGrowFinisherGender(calculation, GenderTypes.BARROW)) /
      calculatedFeederEnergy;

    return {
      boar: amountOfEndFeedBoar,
      gilt: amountOfEndFeedGilt,
      barrow: amountOfEndFeedBarrow,
    };
  }

  getMaxEnergySimulation(calculation: Calculation, simulation: QuickSimulation) {
    const livingWeight = this.getLivingWeight(calculation, simulation);
    const divergingEnergyStart = this.getDivergingEnergyFromGrowthStartStartWeight(
      calculation,
      simulation,
    );
    const divergingEnergyEnd = this.getDivergingEnergyFromGrowthEndDeliveredWeight(
      calculation,
      simulation,
      livingWeight,
      this.getCalculationLivingWeight(calculation),
    );
    let simMaxEnergy = 0;

    const allFeeders = [
      ...calculation.feeders.map((f) => ({ ...f, isCalculated: false })),
      { ...calculation.calculatedFeeder, isCalculated: true },
    ];

    allFeeders
      .map((feeder) => {
        const feederFeed = feeder.genderFeedRatios
          .map((ratio) => ratio.feed)
          .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0);

        let returnFeed = 0;
        switch (feeder.type) {
          case FeederType.START_FEEDER:
            returnFeed = feederFeed / feeder.genderFeedRatios.length + divergingEnergyStart;
            break;
          case FeederType.GROWTH_FEEDER:
            returnFeed = feederFeed / feeder.genderFeedRatios.length;
            break;

          case FeederType.END_FEEDER:
            returnFeed = feederFeed / feeder.genderFeedRatios.length;
            if (feeder.isCalculated) {
              returnFeed += divergingEnergyEnd;
            }
            break;
          default:
            break;
        }
        return {
          actual: returnFeed * feeder.product.ewKg,
          calc: `(${returnFeed} * ${feeder.product.ewKg})`,
        };
      })
      .forEach((feeder) => (simMaxEnergy += feeder.actual));

    return simMaxEnergy;
  }

  // Calculates M50
  getAverageEnergy(
    calculation: Calculation,
    simulation: QuickSimulation,
    maxEnergySimulation?: number,
    pigFeedSum?: number,
  ) {
    maxEnergySimulation =
      maxEnergySimulation ?? this.getMaxEnergySimulation(calculation, simulation);
    pigFeedSum = pigFeedSum ?? this.getPigFeedSum(calculation, simulation);

    return maxEnergySimulation / pigFeedSum;
  }

  // Calculates J25
  getPigletPrice(calculation: Calculation, simulation: QuickSimulation) {
    return simulation.revenuePerKg * simulation.slaughteredWeightInKg - simulation.startWeight < 25
      ? simulation.pigletPrice -
          (25 - simulation.startWeight) * calculation.baseLine.calculatedPriceLessThan25Kg
      : simulation.pigletPrice +
          (simulation.startWeight - 25) * calculation.baseLine.calculatedPriceLargerThan25Kg;
  }

  getAverageEnergyConversion(
    calculation: Calculation,
    simulation: QuickSimulation,
    pigFeedSum?: number,
    averageEnergy?: number,
    livingWeight?: number,
  ) {
    pigFeedSum = pigFeedSum ?? this.getPigFeedSum(calculation, simulation);
    livingWeight = livingWeight ?? this.getLivingWeight(calculation, simulation);
    averageEnergy =
      averageEnergy ?? this.getAverageEnergy(calculation, simulation, undefined, pigFeedSum);

    return (pigFeedSum * averageEnergy) / (livingWeight - simulation.startWeight);
  }

  getEnergyUsed(
    calculation: Calculation,
    simulation: QuickSimulation,
    AverageEnergyConversion?: number,
    livingWeight?: number,
  ) {
    AverageEnergyConversion =
      AverageEnergyConversion ?? this.getAverageEnergyConversion(calculation, simulation);
    livingWeight = livingWeight ?? this.getLivingWeight(calculation, simulation);

    return AverageEnergyConversion * (livingWeight - simulation.startWeight);
  }

  // Calculates J36
  getFeedCosts(
    calculation: Calculation,
    simulation: QuickSimulation,
    EnergyExpended?: number,
    AverageEnergy?: number,
    feedPacketPrice?: number,
  ) {
    feedPacketPrice =
      feedPacketPrice ||
      this.getPacketPriceForFeedCosts(
        calculation,
        simulation,
        this.getLivingWeight(calculation, simulation),
        this.getCalculationLivingWeight(calculation),
      );
    EnergyExpended = EnergyExpended ?? this.getEnergyUsed(calculation, simulation);
    AverageEnergy = AverageEnergy ?? this.getAverageEnergy(calculation, simulation);

    return (EnergyExpended / AverageEnergy) * (feedPacketPrice * 0.01);
  }

  getProfitGrowFinisher(simulation: QuickSimulation) {
    return simulation.revenuePerKg * simulation.slaughteredWeightInKg;
  }

  getProfitGrowFinisherCalculation(calculation: Calculation) {
    return (
      calculation.extendedBaseLine.revenuePerKg * calculation.extendedBaseLine.slaughteredWeightInKg
    );
  }

  // Calculates J37, F37
  getCostDeathsAndSpoilage(
    calculation: Calculation,
    simulation: QuickSimulation,
    avgAmountDeliveredGrowFinisher?: number,
    averageEnergyConversion?: number,
    averageEnergy?: number,
    feedCosts?: number,
    profitGrowFinisher?: number,
    feedPacketPrice?: number,
  ) {
    const livingWeight = this.getLivingWeight(calculation, simulation);

    if (!avgAmountDeliveredGrowFinisher) {
      const amountofDays = this.getAmountOfDays(
        calculation,
        simulation,
        livingWeight,
        this.getCalculationLivingWeight(calculation),
      );
      const totalGrowth = this.getTotalGrowth(calculation, simulation, livingWeight, amountofDays);
      avgAmountDeliveredGrowFinisher = this.getAverageDeliveredGrowFinisher(
        calculation,
        simulation,
        this.getOccupancyRate(calculation, simulation, livingWeight, totalGrowth),
      );
    }
    averageEnergyConversion =
      averageEnergyConversion ?? this.getAverageEnergyConversion(calculation, simulation);
    averageEnergy = averageEnergy ?? this.getAverageEnergy(calculation, simulation);
    feedCosts =
      feedCosts ||
      this.getFeedCosts(calculation, simulation, undefined, averageEnergy, feedPacketPrice);
    profitGrowFinisher = profitGrowFinisher ?? this.getProfitGrowFinisher(simulation);
    feedPacketPrice =
      feedPacketPrice ||
      this.getPacketPrice(
        calculation,
        simulation,
        livingWeight,
        this.getCalculationLivingWeight(calculation),
      );

    return (
      (avgAmountDeliveredGrowFinisher *
        this.genericCalculationsService.toExcelPercentage(
          calculation.extendedBaseLine.mortalityPercentage,
        ) *
        simulation.pigletPrice +
        (((avgAmountDeliveredGrowFinisher *
          this.genericCalculationsService.toExcelPercentage(
            calculation.extendedBaseLine.mortalityPercentage,
          ) *
          (calculation.baseLine.averageWeightPerDeadGrowFinisher - simulation.startWeight) *
          averageEnergyConversion) /
          averageEnergy) *
          feedPacketPrice) /
          100) /
        avgAmountDeliveredGrowFinisher +
      (avgAmountDeliveredGrowFinisher *
        this.genericCalculationsService.toExcelPercentage(
          calculation.extendedBaseLine.mortalityPercentage,
        ) *
        (profitGrowFinisher - simulation.pigletPrice - feedCosts)) /
        avgAmountDeliveredGrowFinisher
    );
  }

  // Calculates J38, F38
  getFinalDelivered(
    calculation: Calculation,
    simulation: QuickSimulation,
    profitGrowFinisher?: number,
    feedCosts?: number,
    costsDeathsAndSpoilage?: number,
    pigletPrice?: number,
  ) {
    profitGrowFinisher = profitGrowFinisher ?? this.getProfitGrowFinisherCalculation(calculation);
    feedCosts = feedCosts ?? this.getFeedCosts(calculation, simulation);
    costsDeathsAndSpoilage =
      costsDeathsAndSpoilage ?? this.getCostDeathsAndSpoilage(calculation, simulation);
    pigletPrice = pigletPrice ?? this.getPigletPrice(calculation, simulation);

    return profitGrowFinisher - pigletPrice - feedCosts - costsDeathsAndSpoilage;
  }

  getManureCubeProduction(
    calculation: Calculation,
    simulation: QuickSimulation,
    totalGrowth?: number,
    feedConversion?: number,
    PigFeedSum?: number,
    livingWeight?: number,
    amountOfDays?: number,
    divergingEnergyStart?: number,
    divergingEnergyEnd?: number,
  ) {
    livingWeight = livingWeight ?? this.getLivingWeight(calculation, simulation);
    totalGrowth =
      totalGrowth ?? this.getTotalGrowth(calculation, simulation, livingWeight, amountOfDays);
    PigFeedSum = PigFeedSum ?? this.getPigFeedSum(calculation, simulation);
    feedConversion = feedConversion ?? this.getFeedConversion(simulation, PigFeedSum, livingWeight);
    divergingEnergyStart =
      divergingEnergyStart ||
      this.getDivergingEnergyFromGrowthStartStartWeight(calculation, simulation);
    divergingEnergyEnd =
      divergingEnergyEnd ||
      this.getDivergingEnergyFromGrowthEndDeliveredWeight(
        calculation,
        simulation,
        livingWeight,
        this.getCalculationLivingWeight(calculation),
      );

    const feedInKG = (totalGrowth / 1000) * feedConversion * 365;
    const allFeeders = [
      ...calculation.feeders.map((f) => ({ ...f, isCalculated: false })),
      { ...calculation.calculatedFeeder, isCalculated: true },
    ];

    const feedProductContributions = allFeeders.map((feeder) => {
      const feederFeed = feeder.genderFeedRatios
        .map((ratio) => ratio.feed ?? 0)
        .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0);
      let returnFeed = 0;
      switch (feeder.type) {
        case FeederType.START_FEEDER:
          returnFeed = feederFeed / feeder.genderFeedRatios.length + divergingEnergyStart;
          break;
        case FeederType.GROWTH_FEEDER:
          returnFeed = feederFeed / feeder.genderFeedRatios.length;
          break;
        case FeederType.END_FEEDER:
          returnFeed = feederFeed / feeder.genderFeedRatios.length;
          if (feeder.isCalculated) {
            returnFeed += divergingEnergyEnd;
          }
          break;
        default:
          break;
      }
      return {
        product: feeder.product,
        contributionPercentage: (returnFeed ?? 0) / PigFeedSum,
      };
    });

    let reSum = 0;
    feedProductContributions.forEach(
      (feeder) => (reSum += feeder.product.proteinGramPerKg * feeder.contributionPercentage),
    );
    const reFeed = feedInKG * reSum;
    const meatProduct = (totalGrowth / 1000) * 365;
    const reMeat = meatProduct * 25 * 6.25;
    const reRemainder = reFeed - reMeat;

    const urineProduction =
      0.0045 * reRemainder +
      0.035 * FEED_PROFIT_MANURE_VALUES.K * feedInKG +
      FEED_PROFIT_MANURE_VALUES.Na * feedInKG * 0.055;
    const fecesProduction = feedInKG * 0.16 * 0.88 * 2.2;
    return (urineProduction + fecesProduction) / 1000;
  }

  getManureCostPerPig(
    calculation: Calculation,
    simulation: QuickSimulation,
    totalGrowth?: number,
    feedConversion?: number,
    PigFeedSum?: number,
    livingWeight?: number,
    amountOfDays?: number,
    divergingEnergyStart?: number,
    divergingEnergyEnd?: number,
  ) {
    livingWeight = livingWeight ?? this.getLivingWeight(calculation, simulation);
    totalGrowth =
      totalGrowth ?? this.getTotalGrowth(calculation, simulation, livingWeight, amountOfDays);
    PigFeedSum = PigFeedSum ?? this.getPigFeedSum(calculation, simulation);
    feedConversion = feedConversion ?? this.getFeedConversion(simulation, PigFeedSum, livingWeight);
    divergingEnergyStart =
      divergingEnergyStart ||
      this.getDivergingEnergyFromGrowthStartStartWeight(calculation, simulation);
    divergingEnergyEnd =
      divergingEnergyEnd ||
      this.getDivergingEnergyFromGrowthEndDeliveredWeight(
        calculation,
        simulation,
        livingWeight,
        this.getCalculationLivingWeight(calculation),
      );

    const manureCubedProduction = this.getManureCubeProduction(
      calculation,
      simulation,
      totalGrowth,
      feedConversion,
      PigFeedSum,
      livingWeight,
      amountOfDays,
      divergingEnergyStart,
      divergingEnergyEnd,
    );
    const priceForCubeOfManure = calculation.extendedBaseLine.fertilizerPricePerCubicMeter;

    return manureCubedProduction * priceForCubeOfManure;
  }

  getFeedProfitAverageGrowFinisherYear(
    calculation: Calculation,
    simulation: QuickSimulation,
    amountOfRounds?: number,
    profitGrowFinisher?: number,
    pigletPrice?: number,
    feedCosts?: number,
    costsDeathsAndSpoilage?: number,
  ) {
    amountOfRounds =
      amountOfRounds ||
      this.getRounds(
        calculation,
        simulation,
        this.getAmountOfDays(calculation, simulation),
        this.getOccupancyRate(calculation, simulation),
      );
    profitGrowFinisher = profitGrowFinisher ?? this.getProfitGrowFinisher(simulation);
    pigletPrice = pigletPrice ?? this.getPigletPrice(calculation, simulation);
    feedCosts = feedCosts ?? this.getFeedCosts(calculation, simulation);
    costsDeathsAndSpoilage =
      costsDeathsAndSpoilage ||
      this.getCostDeathsAndSpoilage(
        calculation,
        simulation,
        undefined,
        undefined,
        undefined,
        feedCosts,
        profitGrowFinisher,
        undefined,
      );

    const feedProfitAverageGrowFinisherInYear = amountOfRounds * profitGrowFinisher;
    const pigletPriceInYear = amountOfRounds * pigletPrice;
    const feedCostsInYear = amountOfRounds * feedCosts;
    const costsDeathsAndSpoilageInYear = amountOfRounds * costsDeathsAndSpoilage;

    return (
      feedProfitAverageGrowFinisherInYear -
      pigletPriceInYear -
      feedCostsInYear -
      costsDeathsAndSpoilageInYear
    );
  }

  getFeedProfitPerSquareMeterIncludingFertilizer(
    calculation: Calculation,
    simulation: QuickSimulation,
    feedprofitAverageGrowFinisherYear?: number,
    manureCostPerPig?: number,
  ) {
    feedprofitAverageGrowFinisherYear =
      feedprofitAverageGrowFinisherYear ||
      this.getFeedProfitAverageGrowFinisherYear(calculation, simulation);
    manureCostPerPig = manureCostPerPig ?? this.getManureCostPerPig(calculation, simulation);

    return (
      (feedprofitAverageGrowFinisherYear - manureCostPerPig) /
      calculation.extendedBaseLine.squareMeterPerGrowFinisher
    );
  }

  getFeedProfitAverageDeliveredGrowFinisher(
    calculation: Calculation,
    simulation: QuickSimulation,
    profitGrowFinisher?: number,
    pigletPrice?: number,
    feedCosts?: number,
    costsDeathsAndSpoilage?: number,
  ) {
    profitGrowFinisher = profitGrowFinisher ?? this.getProfitGrowFinisher(simulation);
    pigletPrice = pigletPrice ?? this.getPigletPrice(calculation, simulation);
    feedCosts = feedCosts ?? this.getFeedCosts(calculation, simulation);
    costsDeathsAndSpoilage =
      costsDeathsAndSpoilage ||
      this.getCostDeathsAndSpoilage(
        calculation,
        simulation,
        undefined,
        undefined,
        undefined,
        feedCosts,
        profitGrowFinisher,
        undefined,
      );

    return profitGrowFinisher - pigletPrice - feedCosts - costsDeathsAndSpoilage;
  }

  getFeedProfitAveragePresentGrowFinisher(
    calculation: Calculation,
    simulation: QuickSimulation,
    amountOfRounds?: number,
    profitPerAverageDeliveredGrowFinisher?: number,
    pigletPrice?: number,
    feedCosts?: number,
    deathsCost?: number,
  ) {
    amountOfRounds = amountOfRounds ?? this.getRounds(calculation, simulation);
    profitPerAverageDeliveredGrowFinisher =
      profitPerAverageDeliveredGrowFinisher ?? this.getProfitGrowFinisher(simulation);
    pigletPrice = pigletPrice ?? this.getPigletPrice(calculation, simulation);
    feedCosts = feedCosts ?? this.getFeedCosts(calculation, simulation);
    deathsCost = deathsCost ?? this.getCostDeathsAndSpoilage(calculation, simulation);

    return (
      profitPerAverageDeliveredGrowFinisher * amountOfRounds -
      pigletPrice * amountOfRounds -
      feedCosts * amountOfRounds -
      deathsCost * amountOfRounds
    );
  }

  generateMappedCalculation(
    calculation: Calculation,
    simulation: QuickSimulation,
    discounts: Discount,
  ) {
    const calculatedFeederRatios = this.calculateRemainingEndFeederRatios(calculation);
    //occupancyRate calculation
    calculation = {
      ...calculation,
      feeders: this.genericCalculationsService.getCalculatedFeedersWithDiscounts(
        calculation.feeders,
        discounts,
      ),
      calculatedFeeder: {
        ...calculation.calculatedFeeder,
        product: {
          ...calculation.calculatedFeeder?.product,
          price: this.genericCalculationsService.getProductPriceWithDiscounts(
            calculation.calculatedFeeder?.product.grossPricePer100Kg,
            discounts,
          ),
        },
        genderFeedRatios: [
          {
            name: GenderTypes.GILT,
            feed: calculatedFeederRatios.gilt ?? 0,
          },
          calculation.genders === GenderOptions.BOAR_GILT
            ? {
                name: GenderTypes.BOAR,
                feed: calculatedFeederRatios.boar ?? 0,
              }
            : {
                name: GenderTypes.BARROW,
                feed: calculatedFeederRatios.barrow ?? 0,
              },
        ],
      },
      comparisonSimulation: this.getComparisonSimulation(calculation, discounts),
    };
    return <Calculation>{
      ...calculation,
      extendedBaseLine: {
        ...calculation.extendedBaseLine,
        fertilizerPerGrowFinisherInCubicMeters: this.getManureCubeProduction(
          calculation,
          simulation,
        ),
      },
    };
  }

  calculateSimulation(
    calculation: Calculation,
    simulation: QuickSimulation,
    index: number,
    discounts?: Discount,
  ) {
    calculation = this.generateMappedCalculation(calculation, simulation, discounts);
    simulation = {
      ...simulation,
      calculatedFeedPackagePrice: simulation.feedPackagePrice,
    };

    const feedPackagePrice = this.genericCalculationsService.roundToExcelStandards(
      this.getPacketPrice(calculation, simulation),
    );

    const calculationLivingWeight = this.genericCalculationsService.roundToExcelStandards(
      this.getCalculationLivingWeight(calculation),
    );
    const livingWeight = this.genericCalculationsService.roundToExcelStandards(
      this.getLivingWeight(calculation, simulation),
    );
    const amountOfDays = this.genericCalculationsService.roundToExcelStandards(
      this.getAmountOfDays(calculation, simulation, livingWeight, calculationLivingWeight),
    );

    const totalgrowth = this.genericCalculationsService.roundToExcelStandards(
      this.getTotalGrowth(calculation, simulation, livingWeight, amountOfDays),
    );
    const occupancyRate = this.genericCalculationsService.roundToExcelStandards(
      this.getOccupancyRate(calculation, simulation, livingWeight, totalgrowth),
    );

    //feed conversion calculation
    const PigFeedSum = this.genericCalculationsService.roundToExcelStandards(
      this.getPigFeedSum(calculation, simulation),
    );
    const feedConversion = this.getFeedConversion(simulation, PigFeedSum, livingWeight);

    //feed price calculation
    const feedPrice = this.getFeedPrice(
      calculation,
      simulation,
      PigFeedSum,
      livingWeight,
      feedPackagePrice,
    );

    //final difference delivered setup calculation
    const averageEnergy = this.getAverageEnergy(calculation, simulation);
    const avergeEnergyConversion = this.getAverageEnergyConversion(
      calculation,
      simulation,
      PigFeedSum,
      averageEnergy,
      livingWeight,
    );
    const feedCosts = this.getFeedCosts(
      calculation,
      simulation,
      undefined,
      averageEnergy,
      undefined,
    );

    //costs death and spoilage calculation
    const pigletPrice = this.getPigletPrice(calculation, simulation);
    const avgAmountDeliveredGrowFinisher = this.getAverageDeliveredGrowFinisher(
      calculation,
      simulation,
      occupancyRate,
    );
    const profitGrowFinisher = this.getProfitGrowFinisher(simulation);
    const costsDeathsAndSpoilage = this.getCostDeathsAndSpoilage(
      calculation,
      simulation,
      avgAmountDeliveredGrowFinisher,
      avergeEnergyConversion,
      averageEnergy,
      feedCosts,
      profitGrowFinisher,
      feedPackagePrice,
    );

    //only needs to be calculated if it isnt the first comparison line
    let finalDifferenceDelivered = 0;
    let finalDifferenceAveragePresentGrowFinisher = 0;
    let finalDifferenceYear = 0;
    const feedProfitYear = 0;

    //feed profit calculations
    const feedProfitAverageGrowFinisher = this.getFeedProfitAverageDeliveredGrowFinisher(
      calculation,
      simulation,
      profitGrowFinisher,
      pigletPrice,
      feedCosts,
      costsDeathsAndSpoilage,
    );
    const feedProfitDelivered = feedProfitAverageGrowFinisher;

    const amountOfRounds = this.getRounds(calculation, simulation, amountOfDays, occupancyRate);
    const feedProfitAverageGrowFinisherInYear = amountOfRounds * profitGrowFinisher;
    const pigletPriceInYear = amountOfRounds * pigletPrice;
    const feedCostsInYear = amountOfRounds * feedCosts;
    const costsDeathsAndSpoilageInYear = amountOfRounds * costsDeathsAndSpoilage;
    const feedprofitAverageGrowFinisherYear =
      feedProfitAverageGrowFinisherInYear -
      pigletPriceInYear -
      feedCostsInYear -
      costsDeathsAndSpoilageInYear;

    //manure related calculations
    const manureCostPerPig = this.getManureCostPerPig(
      calculation,
      simulation,
      totalgrowth,
      feedConversion,
      PigFeedSum,
      livingWeight,
      amountOfDays,
    );
    const feedProfitPerSquareMeterIncludingFertilizer =
      (feedprofitAverageGrowFinisherYear - manureCostPerPig) /
      calculation.extendedBaseLine.squareMeterPerGrowFinisher;

    if (index !== 0) {
      const comparisonSimFeedProfitPerSquareMeterIncludingFertilizer =
        this.getFeedProfitPerSquareMeterIncludingFertilizer(
          calculation,
          calculation.comparisonSimulation,
        );
      const comparisonSimManureCostPerPig = this.getManureCostPerPig(
        calculation,
        calculation.comparisonSimulation,
      );
      const comparisonSimDeliveredGrowFinisher = this.getAverageDeliveredGrowFinisher(
        calculation,
        calculation.comparisonSimulation,
      );
      const comparisonSimFeedProfitAveragePresentGrowFinisher =
        this.getFeedProfitAveragePresentGrowFinisher(calculation, calculation.comparisonSimulation);
      const feedProfitAveragePresentGrowFinisher = this.getFeedProfitAveragePresentGrowFinisher(
        calculation,
        simulation,
        amountOfRounds,
        profitGrowFinisher,
        pigletPrice,
        feedCosts,
        costsDeathsAndSpoilage,
      );

      const deliveredSimulation = this.getFinalDelivered(
        calculation,
        simulation,
        profitGrowFinisher,
        feedCosts,
        costsDeathsAndSpoilage,
        pigletPrice,
      );

      const deliveredComparison = this.getFinalDelivered(
        calculation,
        calculation.comparisonSimulation,
      );

      finalDifferenceDelivered = deliveredSimulation - deliveredComparison;

      finalDifferenceYear =
        ((comparisonSimFeedProfitAveragePresentGrowFinisher - comparisonSimManureCostPerPig) *
          comparisonSimDeliveredGrowFinisher -
          (feedProfitAveragePresentGrowFinisher - manureCostPerPig) *
            avgAmountDeliveredGrowFinisher) *
        -1;

      finalDifferenceAveragePresentGrowFinisher =
        feedProfitPerSquareMeterIncludingFertilizer -
        comparisonSimFeedProfitPerSquareMeterIncludingFertilizer;
    }

    return {
      isComparisonSim: index === 0,
      showFullWidthColumn: false,
      text: '',

      id: simulation.id ?? index,
      startWeight: simulation.startWeight,
      pigletPrice: simulation.pigletPrice,
      slaughteredWeightInKg: simulation.slaughteredWeightInKg,
      revenuePerKg: simulation.revenuePerKg,
      feedPackagePrice,
      occupancyRate,
      amount: amountOfRounds,
      growth: totalgrowth,
      feedConversion,
      feedPrice,
      finalDifferenceDelivered,
      finalDifferenceAveragePresentGrowFinisher,
      finalDifferenceYear,
      feedProfitDelivered,
      feedProfitYear,
      feedProfitAverage: feedProfitPerSquareMeterIncludingFertilizer,
    };
  }

  calculateSimulations(calculation: Calculation, discounts: Discount) {
    const comparisonSim = { ...calculation.comparisonSimulation, isComparisonSim: true };
    const allsimulations = [comparisonSim, ...calculation.quickSimulations];
    const simulations = allsimulations?.map((simulation: QuickSimulation, i: number) =>
      this.calculateSimulation(calculation, simulation, i, discounts),
    );

    return simulations;
  }

  getComparisonSimulation(calculation: Calculation, discounts: Discount, canBeCalculated = true) {
    const incompleteSimulation = <QuickSimulation>{
      id: -1,
      startWeight: calculation?.extendedBaseLine?.startWeight,
      slaughteredWeightInKg: calculation?.extendedBaseLine?.slaughteredWeightInKg,
      revenuePerKg: calculation?.extendedBaseLine?.revenuePerKg,
      pigletPrice: calculation?.extendedBaseLine?.growFinisherPrice,
      calculatedFeedPackagePrice: null,
      feedPackagePrice: calculation?.comparisonSimulation?.feedPackagePrice,
    };
    let packagePrice = incompleteSimulation.feedPackagePrice;
    if (canBeCalculated) {
      const calculatedFeederRatios = this.calculateRemainingEndFeederRatios(calculation);
      const packagePriceCalculation = {
        ...calculation,
        comparisonSimulation: incompleteSimulation,
        ...calculation,
        feeders: this.genericCalculationsService.getCalculatedFeedersWithDiscounts(
          calculation.feeders,
          discounts,
        ),
        calculatedFeeder: {
          ...calculation.calculatedFeeder,
          product: {
            ...calculation.calculatedFeeder?.product,
            price: this.genericCalculationsService.getProductPriceWithDiscounts(
              calculation.calculatedFeeder?.product?.grossPricePer100Kg,
              discounts,
            ),
          },
          genderFeedRatios: [
            {
              name: GenderTypes.GILT,
              feed: calculatedFeederRatios.gilt ?? 0,
            },
            calculation.genders === GenderOptions.BOAR_GILT
              ? {
                  name: GenderTypes.BOAR,
                  feed: calculatedFeederRatios.boar ?? 0,
                }
              : {
                  name: GenderTypes.BARROW,
                  feed: calculatedFeederRatios.barrow ?? 0,
                },
          ],
        },
      };

      packagePrice = this.getPacketPrice(packagePriceCalculation, incompleteSimulation);
    }
    return <QuickSimulation>{
      ...incompleteSimulation,
      calculatedFeedPackagePrice: packagePrice,
      feedPackagePrice: packagePrice,
    };
  }
}
